// MyDownloads.js
import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { API_URL } from '../api';
import { AuthContext } from '../context/AuthContext';
import './MyDownloads.css';

// Book icon for Read Online button
const BookOpenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '6px', verticalAlign: 'middle'}}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

function MyDownloads() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEpubHelp, setShowEpubHelp] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (user) {
      fetchDownloads();
    }
  }, [user, authLoading, navigate]);

  const fetchDownloads = async () => {
    try {
      const response = await api.get('/api/my-downloads/');
      // Handle both { downloads: [...] } and [...] response formats
      const downloadsData = response.data.downloads || response.data;
      setDownloads(Array.isArray(downloadsData) ? downloadsData : []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching downloads:', err);
      setError('Failed to load your downloads');
      setLoading(false);
    }
  };

  const handleDownload = (token, outputFormat = 'original') => {
    let url;
    if (outputFormat === 'epub') {
      url = `${API_URL}/api/download/${token}/?output=epub`;
    } else if (outputFormat === 'pdf') {
      url = `${API_URL}/api/download/${token}/?output=pdf`;
    } else {
      url = `${API_URL}/api/download/${token}/`;
    }
    window.open(url, '_blank');
  };

  if (authLoading || loading) {
    return (
      <div className="downloads-container">
        <div className="loading">Loading your downloads...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="downloads-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="downloads-container">
      <h1>My Downloads</h1>
      
      {downloads.length === 0 ? (
        <div className="no-downloads">
          <p>You haven't redeemed any products yet.</p>
          <Link to="/products" className="browse-btn">Browse Products</Link>
        </div>
      ) : (
        <div className="downloads-list">
          {downloads.map(download => (
            <div key={download.id} className="download-card">
              <div className="download-thumbnail">
                {download.product.thumbnail_url ? (
                  <img src={download.product.thumbnail_url} alt={download.product.title} />
                ) : (
                  <div className="placeholder-thumb">📚</div>
                )}
              </div>
              <div className="download-info">
                <h3>{download.product.title}</h3>
                <p className="download-meta">
                  Redeemed: {new Date(download.downloaded_at).toLocaleDateString()}
                </p>
                {download.promo_code && (
                  <p className="download-promo">Promo: {download.promo_code}</p>
                )}
              </div>
              <div className="download-actions">
                {download.product.product_type === 'ebook' && (
                  <button 
                    onClick={() => navigate(`/read/${download.download_token}`)}
                    className="download-btn read-online-btn"
                    title="Read this book in your browser"
                  >
                    <BookOpenIcon /> Read Online
                  </button>
                )}
                <button 
                  onClick={() => handleDownload(download.download_token)}
                  className="download-btn"
                >
                  ⬇️ Original
                </button>
                {download.product.product_type === 'ebook' && (
                  <>
                    <div className="epub-download-group">
                      <button 
                        onClick={() => handleDownload(download.download_token, 'epub')}
                        className="download-btn epub-btn"
                        title="Download personalized EPUB e-book"
                      >
                        📚 EPUB
                      </button>
                      <button 
                        onClick={() => setShowEpubHelp(true)}
                        className="epub-help-btn"
                        title="What is EPUB?"
                      >
                        ?
                      </button>
                    </div>
                    {download.product.has_readable_content && (
                      <button 
                        onClick={() => handleDownload(download.download_token, 'pdf')}
                        className="download-btn pdf-btn"
                        title="Download personalized PDF"
                      >
                        📄 PDF
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EPUB Help Modal */}
      {showEpubHelp && (
        <div className="epub-help-modal-overlay" onClick={() => setShowEpubHelp(false)}>
          <div className="epub-help-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowEpubHelp(false)}>×</button>
            <h2>📚 What is EPUB?</h2>
            <p>
              EPUB is the most widely supported e-book format. It works on almost all e-readers, 
              tablets, phones, and computers. EPUB files automatically adjust text size and layout 
              to fit your screen.
            </p>
            
            <h3>Free EPUB Readers</h3>
            
            <div className="reader-section">
              <h4>🖥️ Desktop (Windows/Mac)</h4>
              <ul>
                <li>
                  <a href="https://calibre-ebook.com/download" target="_blank" rel="noopener noreferrer">
                    Calibre
                  </a> — Free, powerful e-book manager and reader
                </li>
                <li>
                  <a href="https://www.microsoft.com/store/productId/9WZDNCRFJ4JQ" target="_blank" rel="noopener noreferrer">
                    Microsoft Edge
                  </a> — Built-in EPUB support on Windows
                </li>
                <li>
                  <a href="https://www.apple.com/apple-books/" target="_blank" rel="noopener noreferrer">
                    Apple Books
                  </a> — Pre-installed on Mac
                </li>
              </ul>
            </div>
            
            <div className="reader-section">
              <h4>📱 Android</h4>
              <ul>
                <li>
                  <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.books" target="_blank" rel="noopener noreferrer">
                    Google Play Books
                  </a> — Usually pre-installed
                </li>
                <li>
                  <a href="https://play.google.com/store/apps/details?id=com.aldiko.android" target="_blank" rel="noopener noreferrer">
                    Aldiko Book Reader
                  </a> — Popular free reader
                </li>
                <li>
                  <a href="https://play.google.com/store/apps/details?id=org.readera" target="_blank" rel="noopener noreferrer">
                    ReadEra
                  </a> — Ad-free, supports many formats
                </li>
              </ul>
            </div>
            
            <div className="reader-section">
              <h4>🍎 iPhone/iPad</h4>
              <ul>
                <li>
                  <a href="https://apps.apple.com/app/apple-books/id364709193" target="_blank" rel="noopener noreferrer">
                    Apple Books
                  </a> — Pre-installed on all iOS devices
                </li>
                <li>
                  <a href="https://apps.apple.com/app/kybook-3-ebook-reader/id1348198785" target="_blank" rel="noopener noreferrer">
                    KyBook 3
                  </a> — Feature-rich free reader
                </li>
              </ul>
            </div>
            
            <p className="help-tip">
              <strong>Tip:</strong> After downloading the EPUB file, open it with your preferred reader app. 
              Most devices will automatically suggest an app when you tap the downloaded file.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyDownloads;
